import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #locked = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    this.#locked = true;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Une modale imbriquée survivrait à notre fermeture : plus rendue (son
    // ancêtre passe en display:none) mais toujours dans le top layer, donc
    // rendant tout le document inerte. La page paraît normale et ne répond
    // plus à un seul clic. On ne se ferme jamais en laissant l'une ouverte.
    for (const nested of dialog.querySelectorAll('dialog[open]')) {
      if (nested instanceof HTMLDialogElement) nested.close();
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Le verrou (position:fixed + memorisation du scroll) n'est engage que par
    // showDialog() (modales desktop showModal). Les feuilles mobiles (bottom sheet)
    // ouvrent le <dialog> via .show() natif, sans passer par showDialog() : le body
    // n'a jamais ete deplace, donc il ne faut ni le reinitialiser ni reimposer un
    // scrollTo (qui ramenait sinon la page tout en haut, #previousScrollY valant 0).
    if (this.#locked) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      // Quand le dialog (recherche, panier…) etait ouvert, le scroll-lock met le body en
      // position:fixed. Si on rouvre/ferme tout en HAUT de page (previousScrollY === 0), le
      // scrollTo ci-dessus ne change pas la position -> AUCUN evenement "scroll" n'est emis,
      // donc le header ne recalcule jamais son etat sticky et reste sur son fond solide
      // (blanc) herite de la phase verrouillee, jusqu'a ce qu'un autre evenement le reveille.
      // On force donc un recalcul des listeners de scroll a la fermeture.
      document.dispatchEvent(new Event('scroll'));

      this.#locked = false;
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    // Échap appartient à la modale la plus haute du top layer. Si une modale
    // imbriquée est ouverte au-dessus (fenêtre du bloc lettre dans le tiroir
    // panier, lightbox…), on lui laisse la main : l'intercepter ici fermait le
    // parent tout en laissant l'imbriquée ouverte et invisible, ce qui rendait
    // le document entier inerte (header non cliquable).
    const target = event.target instanceof Element ? event.target : null;
    const innermost = target?.closest('dialog[open]');
    if (innermost && innermost !== this.refs.dialog) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// ── Garde-fou bfcache / navigation ───────────────────────────────────────────
// Si une modale (recherche, panier) ou le menu est ouvert au moment d'une
// navigation, le scroll-lock (body{position:fixed; top:-Npx} + <html scroll-lock>)
// n'est pas nettoyé (closeDialog ne tourne pas) et reste FIGÉ dans la page mise
// en cache. Au retour navigateur (bfcache), la page réapparaît avec le body
// décalé hors écran et/ou la modale plein écran (blanche en mobile) -> PAGE
// BLANCHE. On relâche donc le verrou et on referme les modales à la restauration.
const releaseScrollLock = () => {
  const body = document.body;
  if (body.style.position === 'fixed') {
    const top = parseInt(body.style.top || '0', 10) || 0;
    body.style.width = '';
    body.style.position = '';
    body.style.top = '';
    if (top) window.scrollTo({ top: -top, behavior: 'instant' });
  }
  document.documentElement.removeAttribute('scroll-lock');
};

const closeStaleOverlays = () => {
  document.querySelectorAll('dialog[open]').forEach((d) => {
    try {
      d.close();
    } catch (e) {
      /* noop */
    }
  });
  document.querySelectorAll('details[scroll-lock][open]').forEach((d) => {
    d.open = false;
  });
};

window.addEventListener('pageshow', (event) => {
  // event.persisted === true => page restaurée depuis le bfcache.
  if (event.persisted) {
    closeStaleOverlays();
    releaseScrollLock();
  }
});

// Filet supplémentaire : on relâche le verrou en quittant la page, pour que
// l'instantané mis en cache soit déjà propre.
window.addEventListener('pagehide', releaseScrollLock);
